import React, { FC, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, useParams, useRouteMatch, Redirect } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { ProjectId } from 'api/models';
import { PageLoading as PageLoadingAtom } from 'components/atoms/loading';
import { Page } from 'components/molecules/page/page';
import { SwitchContentBasedOnAccess } from 'components/organisms/permission-controller';
import { ProjectOverviewPage } from 'components/organisms/projects/project-overview';
import { ProjectTasksPage } from 'components/pages';
import { FormResponseLandingPage } from 'components/pages/project-details/form-responses';
import { ProjectSpacesPage } from 'components/pages/project-details/project-spaces/project-spaces';
import { PermissionRuleName, RoutePaths } from 'constant';
import { CurrentProjectContext } from 'contexts';
import { useProjectDetail } from 'data/projects';
import { getStatusUpdates, selectStatusUpdates } from 'data/status-updates';
import { useProjectTasks } from 'data/tasks/hooks';
import { useDistractionFreeMode } from 'hooks/use-distraction-free-mode';
import { useTranslation } from 'hooks/use-translation';
import { useProjectSpacesQuery } from 'queries';
import { useQueryFormInstanceByProjectKey } from 'queries/form-instance';
import { useProjectGlobalSubscription } from 'queries/project-global-conversation/project-global-conversation';
import { SecureRoute } from 'routes/route-with-permission-check';
import { getProjectPlanUrl, getProjectTasksUrl } from 'utils/urls';
import { ProjectDetailError } from '../error';
import { ProjectConversationPage } from './conversation';
import { ProjectConversationPageResponsive } from './conversation-responsive';
import { ProjectFilesPage } from './files';
import { ProjectFinancialsRoute } from './financials';
import { ProjectPlanPage } from './plan';
import { RedirectToSpaceById, RedirectToSpaceTabById } from './spaces/spaces';
import { ProjectStatusUpdatesPage } from './status-updates';

interface ParamTypes {
  projectId: string;
}

export const PageLoading = styled(PageLoadingAtom)<{ pinned: boolean }>`
  ${ifProp(
    'pinned',
    css`
      left: calc(50% + 128px);
    `,
  )};
`;

const Loading = () => {
  const { active } = useDistractionFreeMode();

  return (
    <Page>
      <PageLoading pinned={active} size="large" />
    </Page>
  );
};

export const ProjectDetailRoute: FC = () => {
  const { path } = useRouteMatch();
  const dispatch = useDispatch();
  const { projectId: _projectId } = useParams<ParamTypes>();
  const projectId = ProjectId(parseInt(_projectId, 10));
  const { data: project, isLoading, error } = useProjectDetail(projectId);

  useProjectGlobalSubscription(projectId[1]);

  useProjectTasks(
    projectId,
  ); /* Always fetch tasks when we use project detail */

  const { data: formResponse } = useQueryFormInstanceByProjectKey(projectId);
  const { data: projectSpacesList } = useProjectSpacesQuery(projectId);
  const statusUpdates = useSelector(selectStatusUpdates);

  useEffect(() => {
    dispatch(
      getStatusUpdates({
        projectId: parseInt(_projectId, 10),
      }),
    );
  }, [dispatch, _projectId]);

  const projectsTitle = useTranslation('projects.title');

  return isLoading ? (
    <Loading />
  ) : project != null ? (
    <CurrentProjectContext.Provider value={project}>
      <Helmet>
        <title>{[project.projectName, projectsTitle].join(' | ')}</title>
      </Helmet>
      <Switch>
        <SecureRoute
          path={RoutePaths.projectOverview}
          ruleName={PermissionRuleName.ProjectOverviewPage}
        >
          <ProjectOverviewPage project={project} />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectFinancials}>
          <ProjectFinancialsRoute project={project} />
        </SecureRoute>
        <SecureRoute
          path={RoutePaths.projectPlan}
          ruleName={PermissionRuleName.ProjectPlanPage}
        >
          <ProjectPlanPage project={project} />
        </SecureRoute>
        <SecureRoute
          path={RoutePaths.projectSpaceTabById}
          ruleName={
            (projectSpacesList ?? []).length > 0
              ? undefined
              : PermissionRuleName.ProjectSpacesPage
          }
        >
          <ProjectSpacesPage project={project} />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectSpaceById}>
          <RedirectToSpaceTabById project={project} />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectSpaces}>
          <RedirectToSpaceById project={project} />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectConversation} exact>
          <ProjectConversationPage project={project} />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectConversationDetails} exact>
          <ProjectConversationPage project={project} />
        </SecureRoute>
        <SecureRoute
          path={RoutePaths.projectConversationDetailsResponsive}
          exact
        >
          <ProjectConversationPageResponsive />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectTasks}>
          <ProjectTasksPage project={project} />
        </SecureRoute>
        <SecureRoute
          path={RoutePaths.projectUpdates}
          exact
          ruleName={
            statusUpdates.length > 0
              ? undefined
              : PermissionRuleName.ProjectStatusUpdatePage
          }
        >
          <ProjectStatusUpdatesPage project={project} />
        </SecureRoute>
        <SecureRoute
          path={RoutePaths.projectUpdateDetail}
          exact
          ruleName={
            statusUpdates.length > 0
              ? undefined
              : PermissionRuleName.ProjectStatusUpdatePage
          }
        >
          <ProjectStatusUpdatesPage project={project} />
        </SecureRoute>
        <SecureRoute
          path={[
            RoutePaths.projectFormResponses,
            RoutePaths.projectFormResponseByInstance,
          ]}
          ruleName={
            (formResponse?.instances ?? []).length > 0
              ? undefined
              : PermissionRuleName.ProjectFormResponsePage
          }
          exact
        >
          <FormResponseLandingPage project={project} />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectFiles} exact>
          <ProjectFilesPage project={project} />
        </SecureRoute>
        <SecureRoute path={path} exact>
          <SwitchContentBasedOnAccess
            ruleName={PermissionRuleName.ProjectPlanPage}
            hasAccess={<Redirect to={getProjectPlanUrl(projectId)} />}
            doesNotHaveAccess={<Redirect to={getProjectTasksUrl(projectId)} />}
          />
        </SecureRoute>
      </Switch>
    </CurrentProjectContext.Provider>
  ) : error ? (
    <ProjectDetailError error={error} />
  ) : null;
};
